/**
 * Timeline Watchers Composable
 * Manages all watchers for timeline reactivity
 */

import { watch, type Ref } from 'vue';
import { PaginatedData, UserData } from '../utils/types';

interface ColumnDisplay {
    userId?: boolean;
    email?: boolean;
}

interface TimelineInstanceAPI {
    isInitialized: () => boolean;
    initializeTimeline: (
        data: PaginatedData | null,
        groupBy: string,
        columnDisplay: ColumnDisplay,
        optionsOverride?: { rawData?: UserData[]; currentPage?: number; pageSize?: number }
    ) => void;
    reinitializeTimeline: (
        data: PaginatedData | null,
        groupBy: string,
        columnDisplay: ColumnDisplay,
        optionsOverride?: { rawData?: UserData[]; currentPage?: number; pageSize?: number }
    ) => void;
    updateTimelineGroups: (
        data: PaginatedData | null,
        groupBy: string,
        columnDisplay: ColumnDisplay,
        optionsOverride?: { rawData?: UserData[]; currentPage?: number; pageSize?: number }
    ) => void;
    destroyTimeline: () => void;
}

interface TimelineDataAPI {
    paginatedData: Ref<PaginatedData | null>; // legacy, will be removed later
    filteredApiData: Ref<PaginatedData | null>;
    isFiltered: Ref<boolean>;
}

interface TimelineWatcherContext {
    props: {
        rowData: UserData[];
        columnDisplay: ColumnDisplay;
        groupBy: string;
    };
    timelineInstance: TimelineInstanceAPI;
    timelineData: TimelineDataAPI;
    isLibraryLoaded: () => boolean;
    pagination: {
        currentPage: Ref<number>;
        pageSize: Ref<number>;
    };
}

/**
 * Sets up all watchers for timeline reactivity
 * @param props - Component props
 * @param timelineInstance - Timeline instance API
 * @param timelineData - Timeline data API
 * @param isLibraryLoaded - Function to check if vis library is loaded
 */
export const useTimelineWatchers = (ctx: TimelineWatcherContext) => {
    const { props, timelineInstance, timelineData, isLibraryLoaded, pagination } = ctx;

    function buildOverride() {
        // Only provide override if we have actual data
        const data = timelineData.filteredApiData.value?.data;
        if (!data || data.length === 0) {
            return undefined; // Let the timeline use default empty initialization
        }
        return {
            rawData: data,
            currentPage: pagination.currentPage.value,
            pageSize: pagination.pageSize.value
        };
    }

    function reinit() {
        timelineInstance.reinitializeTimeline(
            timelineData.paginatedData.value,
            props.groupBy,
            props.columnDisplay,
            buildOverride()
        );
    }

    function init() {
        timelineInstance.initializeTimeline(
            timelineData.paginatedData.value,
            props.groupBy,
            props.columnDisplay,
            buildOverride()
        );
    }

    // Watch for row data changes
    watch(
        () => props.rowData,
        () => {
            // Always ensure library is loaded
            if (!isLibraryLoaded()) return;

            const initialized = timelineInstance.isInitialized();

            // Always initialize or reinitialize timeline (even with empty data) to show header
            if (initialized) {
                reinit();
            } else {
                init();
            }
        },
        { deep: true, immediate: true }
    );

    // Watch for column display changes
    watch(() => props.columnDisplay, () => {
        if (timelineInstance.isInitialized()) {
            timelineInstance.updateTimelineGroups(
                timelineData.paginatedData.value,
                props.groupBy,
                props.columnDisplay,
                buildOverride()
            );
        }
    }, { deep: true });

    // Watch for groupBy changes
    watch(() => props.groupBy, () => {
        if (!timelineInstance.isInitialized()) return;
        if (pagination.currentPage.value !== 1) pagination.currentPage.value = 1;
        reinit();
    });

    // Watch for filtered data changes
    watch(() => timelineData.filteredApiData.value, () => {
        if (!timelineInstance.isInitialized() || !timelineData.isFiltered.value) return;
        reinit();
    }, { deep: true });
};

/**
 * Sets up pagination watcher for timeline
 * @param currentPage - Current page ref
 * @param timelineInstance - Timeline instance API
 * @param timelineData - Timeline data API
 * @param props - Component props
 */
export const useTimelinePaginationWatcher = (ctx: {
    currentPage: Ref<number>;
    pageSize: Ref<number>;
    timelineInstance: TimelineInstanceAPI;
    timelineData: TimelineDataAPI;
    props: { groupBy: string; columnDisplay: ColumnDisplay };
}) => {
    const { currentPage, pageSize, timelineInstance, timelineData, props } = ctx;
    watch(() => currentPage.value, () => {
        if (!timelineInstance.isInitialized()) return;
        timelineInstance.reinitializeTimeline(
            timelineData.paginatedData.value,
            props.groupBy,
            props.columnDisplay,
            {
                rawData: timelineData.filteredApiData.value?.data || [],
                currentPage: currentPage.value,
                pageSize: pageSize.value
            }
        );
    });
    watch(() => pageSize.value, () => {
        if (!timelineInstance.isInitialized()) return;
        // Reset to page 1 when page size changes (common UX pattern)
        currentPage.value = 1;
        timelineInstance.reinitializeTimeline(
            timelineData.paginatedData.value,
            props.groupBy,
            props.columnDisplay,
            {
                rawData: timelineData.filteredApiData.value?.data || [],
                currentPage: currentPage.value,
                pageSize: pageSize.value
            }
        );
    });
};
